import React from "react"
import ReactDOM from "react-dom/client"
import { config } from "@fortawesome/fontawesome-svg-core"
import { createBrowserRouter, redirect, RouterProvider } from "react-router-dom"
import { setNonce } from "get-nonce" // strictly for @radix-ui's dependency on react-remove-scroll

import "./index.css"

import { loadConfig } from "./utils/config"
import { Loader } from "./components/Loader"

import Auth from "./routes/auth"
import Root from "./routes/root"

import MicroServices from "./routes/microservices"
import DocumentServer from "./routes/document-server"
import MicroServicesMessages from "./routes/microservices-messages"
import MicroServicesProcessing from "./routes/microservices-processing"
import MicroServicesStaleProcessing from "./routes/microservices-stale-processing"
import MicroServicesSearch from "./routes/microservices-search"
import MicroServicesHistory from "./routes/microservices-history"
import MicroServicesDivergence from "./routes/microservices-divergence"
import { metadataLoader } from "./utils"

import { setupAuthMachine } from "./machines/AuthMachine"

import { AppInsightsContext } from "@microsoft/applicationinsights-react-js"
import { createInsightsSettings } from "./insights"

/* if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS === "true") {
  const { worker } = await import("./mocks/handlers")
  await worker.start()
} */

const nonceEl = document.querySelector("meta[name=nonce]")
setNonce(nonceEl?.getAttribute("content") ?? "")
nonceEl?.remove()

config.autoAddCss = false // required for CSP

await loadConfig()

setupAuthMachine()

const router = createBrowserRouter([
  {
    path: "/",
    id: "root",
    loader: Root.loader,
    errorElement: <Root.ErrorBoundary />,
    element: <Root />,
    children: [
      {
        index: true,
        loader: () => redirect("microservices"),
      },
      {
        path: "document-server",
        loader: DocumentServer.loader,
        element: <DocumentServer />,
        errorElement: <Root.ErrorBoundary />,
      },
      {
        path: "microservices",
        loader: MicroServices.loader,
        action: MicroServices.action,
        element: <MicroServices />,
        errorElement: <Root.ErrorBoundary />,
      },
      {
        path: "microservices/search",
        loader: MicroServicesSearch.loader,
        element: <MicroServicesSearch />,
        errorElement: <Root.ErrorBoundary />,
      },
      {
        path: "microservices/history",
        loader: MicroServicesHistory.loader,
        element: <MicroServicesHistory />,
        errorElement: <Root.ErrorBoundary />,
      },
      {
        path: "microservices/divergence",
        loader: MicroServicesDivergence.loader,
        element: <MicroServicesDivergence />,
        errorElement: <Root.ErrorBoundary />,
      },
      {
        path: "microservices/processing",
        action: MicroServicesProcessing.action,
        element: <MicroServicesProcessing />,
        errorElement: <Root.ErrorBoundary />,
      },
      {
        path: "microservices/stale-processing",
        action: MicroServicesStaleProcessing.action,
        element: <MicroServicesStaleProcessing />,
        errorElement: <Root.ErrorBoundary />,
      },
      {
        path: "microservices/messages",
        loader: MicroServicesMessages.loader,
        action: MicroServicesMessages.action,
        element: <MicroServicesMessages />,
        errorElement: <Root.ErrorBoundary />,
      },
      {
        path: "microservices/deadLetters",
        loader: MicroServicesMessages.loader,
        action: MicroServicesMessages.action,
        element: <MicroServicesMessages deadLetters />,
        errorElement: <Root.ErrorBoundary />,
      },
      {
        path: "message-metadata",
        loader: metadataLoader,
        errorElement: <Root.ErrorBoundary />,
      },
      {
        path: "process-metadata",
        loader: metadataLoader,
        errorElement: <Root.ErrorBoundary />,
      },
    ],
  },
  { path: "auth", loader: Auth.loader, element: <Auth />, errorElement: <Root.ErrorBoundary /> },
  { path: "/external-login", loader: () => (window.close(), null) },
])

const { reactPlugin, isTelemetryEnabled } = createInsightsSettings()

const appContent = <RouterProvider router={router} fallbackElement={<Loader />} />

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {isTelemetryEnabled ? (
      <AppInsightsContext.Provider value={reactPlugin}>{appContent}</AppInsightsContext.Provider>
    ) : (
      appContent
    )}
  </React.StrictMode>,
)
